import {
  GetNoiseEventsByAlertIdBaseQuery,
  GetNoiseEventsByAlertIdBaseQueryVariables,
  GetNoiseEventsByAlertIdClassificationQuery,
  GetNoiseEventsByAlertIdClassificationQueryVariables,
  GetNoiseEventsByAlertIdResourceGroupsQuery,
  GetNoiseEventsByAlertIdResourceGroupsQueryVariables,
} from 'app/__generated__/global';
import { ApolloProvider } from 'core/net/ApolloProvider';
import Guid from 'core/types/Guid';
import { getDetailedNoiseEvents } from './getDetailedNoiseEvents';
import { GET_NOISE_EVENTS_BY_ALERT_ID_BASE } from './graphql/facility.getNoiseEventsByAlertIdBase';
import { GET_NOISE_EVENTS_BY_ALERT_ID_RESOURCE_GROUPS } from './graphql/facility.getNoiseEventsByAlertIdResourceGroups';
import { GET_NOISE_EVENTS_BY_ALERT_ID_CLASSIFICATION } from './graphql/facility.getNoiseEventsByAlertIdClassification';

type CumulusNoiseEventBase = NonNullable<
  NonNullable<NonNullable<GetNoiseEventsByAlertIdBaseQuery['facility']>['alerts']>['noiseEvents']
>[number];

type CumulusNoiseEventResourceGroups = NonNullable<
  NonNullable<NonNullable<GetNoiseEventsByAlertIdResourceGroupsQuery['facility']>['alerts']>['noiseEvents']
>[number];

type CumulusNoiseEventClassification = NonNullable<
  NonNullable<NonNullable<GetNoiseEventsByAlertIdClassificationQuery['facility']>['alerts']>['noiseEvents']
>[number];

export type CumulusNoiseEvent = CumulusNoiseEventBase &
  Partial<CumulusNoiseEventResourceGroups> &
  Partial<CumulusNoiseEventClassification>;

export type ArenaNoiseEvent = Awaited<ReturnType<typeof getDetailedNoiseEvents>>[number];

export const getNoiseEventsByAlertIdBase = async (alertId: Guid) => {
  const variables = { filter: { id_in: [alertId] } };
  const { data } = await ApolloProvider.global().query<
    GetNoiseEventsByAlertIdBaseQuery,
    GetNoiseEventsByAlertIdBaseQueryVariables
  >({
    query: GET_NOISE_EVENTS_BY_ALERT_ID_BASE,
    variables,
  });
  const cumulusNoiseEvents = data?.facility?.alerts?.noiseEvents ?? [];
  if (!cumulusNoiseEvents.length) return [];
  const noiseEventIds = cumulusNoiseEvents.flatMap(event => event?.id ?? []);
  const arenaNoiseEvents = await getDetailedNoiseEvents({ noiseEventIds });
  return cumulusNoiseEvents.flatMap(event => {
    if (!event) return [];
    const arenaNoiseEvent = arenaNoiseEvents.find(({ noiseEventId }) => noiseEventId === event.id);
    if (!arenaNoiseEvent) return [];
    return {
      ...event,
      ...arenaNoiseEvent,
    };
  });
};

export const getNoiseEventsByAlertIdResourceGroups = async (alertId: Guid) => {
  const variables = { filter: { id_in: [alertId] } };
  const { data, error } = await ApolloProvider.global().query<
    GetNoiseEventsByAlertIdResourceGroupsQuery,
    GetNoiseEventsByAlertIdResourceGroupsQueryVariables
  >({
    query: GET_NOISE_EVENTS_BY_ALERT_ID_RESOURCE_GROUPS,
    variables,
  });
  if (!data || error)
    throw new Error(
      `Something went wrong when trying to get noise events resource groups data by alert ID: ${alertId}`
    );
  const noiseEvents = data?.facility?.alerts?.noiseEvents;
  return noiseEvents ?? [];
};

export const getNoiseEventsByAlertIdClassification = async (alertId: Guid) => {
  const variables = { filter: { id_in: [alertId] } };
  const { data, error } = await ApolloProvider.global().query<
    GetNoiseEventsByAlertIdClassificationQuery,
    GetNoiseEventsByAlertIdClassificationQueryVariables
  >({
    query: GET_NOISE_EVENTS_BY_ALERT_ID_CLASSIFICATION,
    variables,
  });
  if (!data || error)
    throw new Error(`Something went wrong when trying to get noise events classification data by alert ID: ${alertId}`);
  const noiseEvents = data?.facility?.alerts?.noiseEvents;
  return noiseEvents ?? [];
};
